import { useMemo, useState } from 'react';
import { Location, useLocation, useParams } from 'react-router-dom';
import { Button, Drawer, Grid, Modal, Select, Space, Tabs } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
} from '@ant-design/icons';

import { useGetRole, useUserCompaniesData } from '../../../../Hooks/TT_ELD/UserCompanies';
import { userCompany } from '../../../../API/TT_ELD/userCompany';
import { sendEvent } from '../../../../Utils/analytics';
import useRouteQuery from '../../../../Utils/useRouterQuery';
import { useTagQuerySync } from '../../../../Utils/useTagQuerySync';

import UserCompaniesTable from './UserCompaniesTable';
import AddMultiUserModal from './AddMultiUserModal';
import SearchOptions from '../../../../Utils/SearchOptions';
import {
  SearchForUserCompanyDR,
  SearchForUserCompanyEM,
  SearchResultForCompany,
} from '../../../../Utils/SearchResults';

const { useBreakpoint } = Grid;

const UserCompanies = ({ uid }: { uid?: string }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { id } = useParams();
  const [skip, setSkip] = useRouteQuery('1', 'skip', String);
  const [isActive, setIsActive] = useState<boolean | undefined>(true);

  const [driverId, setDriverId] = useState(uid);
  const [companyId, setCompanyId] = useState(uid ? '' : id);
  const [companyName, setCompanyName] = useRouteQuery('', 'companyName', String);
  const [role, setRole] = useRouteQuery('', 'role', String);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalBulk, setIsModalBulk] = useState(false);
  const [open, setOpen] = useState(false);

  const [getKey, setGetKey] = useState<number[] | string | number>([]);
  const [modal, contextHolder] = Modal.useModal();

  const [filtersOpen, setFiltersOpen] = useState(false);

  let location: Location = useLocation();

  useTagQuerySync(
    { en_US: 'User Companies', zh_CN: '' },
    'user-companies',
    { companyName, companyId, role, skip },
    { companyName: setCompanyName, companyId: setCompanyId, role: setRole, skip: setSkip },
  );

  const { data, refetch, isLoading, isFetching } = useUserCompaniesData(
    id,
    companyId,
    role,
    driverId,
    skip,
    isActive,
  );

  const email = data?.data?.[0]?.user?.email;

  const getRole = useGetRole();
  const roleList = getRole?.data?.data
    ? Object.entries(getRole.data.data).map(([value, label]) => ({ value, label }))
    : [];

  const filteredCompanies = data?.data?.map((u: any) => ({
    value: u?.company?.id,
    label: u?.company?.name,
  }));
  const getCMPname = filteredCompanies?.[0];

  const confirmDelete = () => {
    modal.confirm({
      title: 'Delete Companies',
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: async () => {
        try {
          await userCompany.deleteUserCompanyMany({ userCompanyIds: getKey });
          sendEvent('UserCompanies Bulk edit Delete', 'Delete UserCompanies', '', 1);
        } finally {
          refetch();
        }
      },
    });
  };

  const handleSearchSelect =
    (setter: (val: string | undefined) => void, nameSetter?: (val: string) => void) =>
    (val: any, { valId }: { valId: string }) => {
      setter(valId);
      nameSetter?.(val);
      if (valId) setSkip('1');
    };

  const disable = location.pathname === '/user-companies';
  let none = true;
  if (!uid && !disable && id) none = false;

  const filtersUI = useMemo(
    () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        {!uid && (
          <div style={{ width: '100%' }}>
            <SearchOptions
              SearchResult={SearchForUserCompanyDR}
              onSelect={handleSearchSelect(setDriverId)}
              placeholder="Search by username"
              searchWidth="100%"
            />
          </div>
        )}

        {!uid && (
          <div style={{ width: '100%' }}>
            <SearchOptions
              SearchResult={SearchForUserCompanyEM}
              onSelect={handleSearchSelect(setDriverId)}
              placeholder="Search by email"
              searchWidth="100%"
            />
          </div>
        )}

        {none && (
          <div style={{ width: '100%' }}>
            <SearchOptions
              SearchResult={SearchResultForCompany}
              value={companyName}
              onSelect={handleSearchSelect(setCompanyId, setCompanyName)}
              placeholder="Search by Company"
              searchWidth="100%"
            />
          </div>
        )}

        <Select
          showSearch
          allowClear
          style={{ width: '100%' }}
          placeholder="Select role"
          value={role || undefined}
          options={roleList}
          onChange={(val) => {
            setRole(val ?? '');
            setSkip('1');
          }}
          optionFilterProp="children"
          filterOption={(input, option: any) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
        />

        {isMobile && (
          <Space style={{ width: '100%' }} size={10}>
            <Button block onClick={() => setFiltersOpen(false)}>
              Close
            </Button>
            <Button
              block
              type="primary"
              onClick={() => {
                refetch();
                setFiltersOpen(false);
              }}
            >
              Apply & Refresh
            </Button>
          </Space>
        )}
      </div>
    ),
    [uid, none, companyName, role, roleList, isMobile],
  );
  const tabItems = [
    { key: 'active', label: 'Active' },
    { key: 'inactive', label: 'Inactive' },
  ];

  const handleTabChange = (key: string) => {
    if (key === 'active') {
      setIsActive(true);
    } else if (key === 'inactive') {
      setIsActive(false);
    } else {
      setIsActive(undefined);
    }

    setSkip('1');
  };

  return (
    <div className="mainPage">
      {id && (
        <Tabs
          activeKey={isActive ? 'active' : 'inactive'}
          onChange={(key) => {
            if (key === 'active') setIsActive(true);
            else if (key === 'inactive') setIsActive(false);
          }}
          items={[
            {
              label: (
                <span>
                  <CheckCircleOutlined style={{ marginRight: 8 }} />
                  Active
                </span>
              ),
              key: 'active',
            },
            {
              label: (
                <span>
                  <CloseCircleOutlined style={{ marginRight: 8 }} />
                  Inactive
                </span>
              ),
              key: 'inactive',
            },
          ]}
        />
      )}
      {contextHolder}

      {isModalVisible && (
        <AddMultiUserModal
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          userId={driverId}
          companyId={companyId}
          refetch={refetch}
          id={id}
          email={email}
          getCMPname={getCMPname}
        />
      )}

      {/* Toolbar */}
      {!isMobile ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          {/* Filters (desktop inline) */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {!uid && (
              <SearchOptions
                SearchResult={SearchForUserCompanyDR}
                onSelect={handleSearchSelect(setDriverId)}
                placeholder="Search by username"
              />
            )}

            {!uid && (
              <SearchOptions
                SearchResult={SearchForUserCompanyEM}
                onSelect={handleSearchSelect(setDriverId)}
                placeholder="Search by email"
              />
            )}

            {none && (
              <SearchOptions
                SearchResult={SearchResultForCompany}
                value={companyName}
                onSelect={handleSearchSelect(setCompanyId, setCompanyName)}
                placeholder="Search by Company"
              />
            )}

            <Select
              showSearch
              allowClear
              style={{ width: 200 }}
              placeholder="Select role"
              value={role || undefined}
              options={roleList}
              onChange={(val) => {
                setRole(val ?? '');
                setSkip('1');
              }}
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button type="primary" size="large" onClick={() => setIsModalVisible(true)}>
              Add
            </Button>
            <Button size="large" onClick={() => refetch()}>
              Refresh
            </Button>
            {isModalBulk && (
              <Button type="primary" danger size="large" onClick={confirmDelete}>
                Delete All
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <Button
            block
            size="large"
            onClick={() => setFiltersOpen(true)}
            icon={<FilterOutlined />}
          />
          <Button block type="primary" size="large" onClick={() => setIsModalVisible(true)}>
            Add
          </Button>
          <Button block size="large" onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      )}

      <Drawer
        title="Filters"
        placement="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        height="60vh"
      >
        {filtersUI}
      </Drawer>

      {/* Mobile: Delete All alohida (table ustida) */}
      {isMobile && isModalBulk && (
        <div style={{ marginBottom: 10 }}>
          <Button danger block size="large" onClick={confirmDelete}>
            Delete All
          </Button>
        </div>
      )}

      <UserCompaniesTable
        userCompanies={data?.data}
        total={data?.count}
        onChange={(q) => setSkip(q.current)}
        isLoading={isLoading}
        isFetching={isFetching}
        open={open}
        setOpen={setOpen}
        setIsModalBulk={setIsModalBulk}
        refetch={refetch}
        skip={skip}
        setGetKey={setGetKey}
      />
    </div>
  );
};

export default UserCompanies;
